import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Table } from 'react-bootstrap';
import { KTIcon } from '../../../../_theme/helpers/components/KTIcon';
import SimpleLoader from './SimpleLoader';
import { IUser } from '../../core/_models';
import { getEvaluatorUserList } from '../../core/_request';
import { deleteProjectEvaluator, getProjectEvaluators, saveProjectEvaluators } from '../../project/core/_request';
import { useSnackbar } from '../../../../_theme/context/SnackbarContext';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth, usePermission } from '../../auth';
import CustomFormSearchSelect from './CustomFormSearchSelect';
import CustomInputPreview from './CustomInputPreview';
import { isAbortedError } from '../../../../utils/global';

interface ManageEvaluatorModalProps {
    onClose: () => void;
    project_id: string;
}

const ManageEvaluatorModal: React.FC<ManageEvaluatorModalProps> = ({
    onClose,
    project_id,
}) => {
    const { showSnackbar } = useSnackbar();
    const { auth } = useAuth();
    const { can } = usePermission();
    const [submitLoader, setSubmitLoader] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [rows, setRows] = useState<IUser[]>([])
    const [evaluatorUserList, setEvaluatorUserList] = useState<IUser[]>([]);
    const isSelectedElsewhere = rows.map((row: IUser) => row.user_id);
    const [newUsers, setNewUsers] = useState<number[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [projectEvalutionStatus, setProjectEvalutionStatus] = useState("");
    const canEditEvaluators = auth?.user_details?.is_approver !== 'Yes'
        && projectEvalutionStatus !== 'Completed'
        && can('panoptic_evaluation_dashboard', 'write');
    const [selectedData, setSelectedData] = useState<IUser | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchData = async () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        try {
            const [userResponse, eveResponse]: any = await Promise.all([
                getEvaluatorUserList(controller.signal),
                getProjectEvaluators(project_id, controller.signal)
            ]);

            if (controller.signal.aborted) return;

            if (userResponse.status === "ok") {
                setEvaluatorUserList(userResponse.data.list);
            }
            if (eveResponse.status === "ok") {
                setRows(eveResponse.data.project_evaluators);
                setProjectEvalutionStatus(eveResponse.data.project_eval_status);

            }
        } catch (error) {
            if (isAbortedError(error)) return;
            console.error('Failed to fetch projects:', error);
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    };
    useEffect(() => {
        fetchData();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);
    const onConfirm = async () => {
        const blankUsers = rows.filter((row: IUser) => row.user_id === 0);
        if (blankUsers.length > 0) {
            showSnackbar('Select an evaluator or remove the blank row before saving.', 'danger');
            return;
        } else if (newUsers.length == 0) {
            onClose();
            return;
        }
        setSubmitLoader(true);
        const data = {
            project_id: project_id,
            user_id: auth?.user_id,
            evaluator_data: JSON.stringify(newUsers)
        };
        const resp: any = await saveProjectEvaluators(data);
        if (resp.status == "ok") {
            showSnackbar(resp.message, 'success');
            setNewUsers([]);
            onClose();
        } else {
            showSnackbar(resp.message, 'danger');
        }
        setSubmitLoader(false);
    };
    const handleChangeUser = (
        index: number,
        value: number
    ) => {
        const updatedRows = [...rows];
        updatedRows[index].user_id = value;
        updatedRows[index].email = evaluatorUserList.find((user: IUser) => user.user_id == value)?.email || '';
        updatedRows[index].fullname = evaluatorUserList.find((user: IUser) => user.user_id == value)?.fullname || '';
        setRows(updatedRows);
        setNewUsers([...newUsers, value]);
    };

    const removeRow = (row: IUser) => {
        setSelectedData(row);
        setShowDeleteModal(true);
    };

    const handleRemoveBlankRow = (index: number) => {
        setRows((prevRows: IUser[]) =>
            prevRows.filter((_, i) => i !== index)
        );
    };
    const addRow = () => {
        setRows([...rows, { user_id: 0, email: '', fullname: '' }]);
    };
    const handleConfirmDelete = async () => {
        if (selectedData !== null) {
            const data = {
                project_id: Number(project_id),
                evaluator_id: selectedData.user_id
            }
            const resp: any = await deleteProjectEvaluator(data);
            if (resp.status == "ok") {
                showSnackbar(resp.message, 'success');
            } else {
                showSnackbar(resp.message, 'danger');
            }
            const updatedRows = rows.filter((row: IUser) => row.user_id != selectedData.user_id);
            setRows(updatedRows);
            setSelectedData(null);
            setShowDeleteModal(false);
        }
    }
    return (
        <>
            <Modal
                show={true}
                onHide={onClose}
                dialogClassName='modal-dialog modal-lg modal-dialog-centered position-relative'
                backdrop="static" keyboard={false}
            >
                { loading && <SimpleLoader className={"bg-black-30 z-2"} />}
                <div className='modal-header pb-5'>
                    <h2>Manage Evaluators</h2>
                    <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={onClose}>
                        <KTIcon className='fs-1' iconName='cross' />
                    </div>
                </div>
                <div className='modal-body'>
                    <div className='d-flex align-center justify-content-end mb-5'>
                        {canEditEvaluators && <Button
                            variant='light-primary'
                            size='sm'
                            onClick={addRow}
                            title='Add Member'
                        >
                            <KTIcon iconName='plus-square' className='fs-3 pe-0' /> Add Evaluator
                        </Button>}
                    </div>
                    <div className='h-400px overflow-auto'>
                        <table className='table-rounded table-row-bordered mb-10 mt-2 table table-bordered table-hover'>
                            <thead>
                                <tr className='bg-secondary'>
                                    <th className='text-gray-700'>Name</th>
                                    <th className='text-gray-700'>Email</th>
                                    {canEditEvaluators &&
                                        <th className="text-center text-gray-700">
                                            Action
                                        </th>}
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    rows.map((row: IUser, idx: number) => {
                                        return <tr key={idx}>
                                            <td>
                                                {
                                                    (row.user_id !== 0 && !newUsers.includes(row.user_id)) ?
                                                        <CustomInputPreview value={row.fullname} />
                                                        : <CustomFormSearchSelect
                                                            value={row.user_id || ''}
                                                            onChange={(value) => handleChangeUser(idx, value as number)}
                                                            options={evaluatorUserList.map(user => ({
                                                                label: user.fullname,
                                                                value: user.user_id,
                                                                isDisabled: row.user_id != user.user_id && isSelectedElsewhere.includes(user.user_id)
                                                            }))}
                                                            placeholder='Select User'
                                                            usePortal={true}
                                                        />
                                                }

                                            </td>
                                            <td>
                                                <CustomInputPreview value={row.email || '-'} />
                                            </td>
                                            {canEditEvaluators && <td className='text-center'>
                                                <Button
                                                    variant='light-danger'
                                                    size='sm'
                                                    disabled={row.eval_status === "completed"}
                                                    onClick={() => row.user_id ? removeRow(row) : handleRemoveBlankRow(idx)}
                                                >
                                                    <KTIcon iconName='trash' className='fs-3 pe-0' />
                                                </Button>

                                            </td>}
                                        </tr>
                                    })
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
                <Modal.Footer>
                    <Button variant='secondary' onClick={onClose}>
                        Cancel
                    </Button>
                    
                    {canEditEvaluators && <Button
                        variant='primary'
                        onClick={onConfirm}
                        disabled={submitLoader}
                    >
                        Save
                        {submitLoader && <span className='ms-2 spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>}
                    </Button>}
                </Modal.Footer>
            </Modal>
            <DeleteConfirmationModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                deleteMessage={`Evaluator's score will also get deleted along with Evaluator. Do you want to delete ?`}
            />
        </>
    );
};

export default ManageEvaluatorModal;

